import { useMemo } from "react";
import { useTranslation } from "react-i18next";

import { usePagedRows } from "../../../../shared/components/reports";
import { Pagination } from "../../../../shared/ui/common/Pagination";
import {
    subjectKey,
    subjectName,
    type ReportCustomField,
    type ReportLinks,
    type ReportSubjectRow,
} from "../../domain/PractitionerReport";
import MaybeLink from "./MaybeLink";

interface Props {
    fields: ReportCustomField[];
    subjects: ReportSubjectRow[];
    locale: string;
    groupOf: (count: number) => string;
    links: ReportLinks;
}

const HEADING = "py-2 pr-4 font-medium";
const CELL = "py-2.5 pr-4 text-content-primary";

/** A value as read: a date in the reader's language, anything else as stored. */
function formatValue(field: ReportCustomField, value: string | undefined, locale: string): string {
    if (!value) return "—";
    if (field.field_type === "date") {
        const day = new Date(`${value}T00:00:00`);
        return Number.isNaN(day.getTime()) ? value : day.toLocaleDateString(locale);
    }
    return value;
}

/**
 * The centre's own fields for each patient of the report — their student
 * number, their file reference — one row per patient, one column per field.
 * Couples and groups have no such fields and are left out.
 */
export default function CustomFieldsTable({ fields, subjects, locale, groupOf, links }: Props) {
    const { t } = useTranslation();
    const rows = useMemo(() => subjects.filter((row) => row.type === "patient"), [subjects]);
    const paged = usePagedRows(rows);

    return (
        <section className="rounded-2xl border border-gray-200 bg-white p-6 dark:border-gray-800 dark:bg-white/[0.03]">
            <div className="mb-4">
                <h2 className="text-base font-semibold text-content-primary">{t("reports.customFields.title")}</h2>
                <p className="mt-1 text-sm text-content-secondary">{t("reports.customFields.subtitle")}</p>
            </div>
            {rows.length === 0 ? (
                <p className="rounded-xl bg-surface-sunken p-6 text-center text-sm text-content-tertiary">
                    {t("reports.customFields.empty")}
                </p>
            ) : (
                <div className="overflow-x-auto">
                    <table className="w-full text-left text-sm">
                        <thead>
                            <tr className="border-b border-gray-200 text-xs uppercase tracking-wide text-content-tertiary dark:border-gray-800">
                                <th scope="col" className={HEADING}>
                                    {t("reports.customFields.patient")}
                                </th>
                                {fields.map((field) => (
                                    <th key={field.id} scope="col" className={HEADING}>
                                        {field.label}
                                    </th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {paged.page.map((row) => (
                                <tr
                                    key={subjectKey(row.type, row.id)}
                                    className="border-b border-gray-100 last:border-0 dark:border-gray-800"
                                >
                                    <th scope="row" className={`${CELL} font-medium`}>
                                        <MaybeLink to={links.subject(row.type, row.id)}>
                                            {subjectName(row, groupOf)}
                                        </MaybeLink>
                                    </th>
                                    {fields.map((field) => (
                                        <td
                                            key={field.id}
                                            className={field.field_type === "number" ? `${CELL} tabular-nums` : CELL}
                                        >
                                            {formatValue(field, row.custom_values?.[field.id], locale)}
                                        </td>
                                    ))}
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
            <Pagination
                offset={paged.offset}
                total={paged.total}
                pageSize={paged.pageSize}
                onPrev={paged.onPrev}
                onNext={paged.onNext}
            />
        </section>
    );
}
